import { useState, useEffect } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { AppLayout, PageTransition, useHeaderConfig } from "@/components/layout";
import { Icon } from "@/components/ui/Icon";
import { shareUrl } from "@/utils/share";
import {
  clubsApi,
  useGetClubQuery,
  useCheckClubMembershipQuery,
  useFetchClubMembersQuery,
  useFetchPublicClubPaymentSettingsQuery,
  useFetchClubSubscriptionQuery,
  useFetchPublicClubByInviteCodeQuery,
  useFetchCurrentUserProfileQuery,
  useDeleteEventMutation,
  EventType,
  type Event,
  type EventOccurrence,
} from "@/api/clubsApi";
import { ClubPaymentButton, MembershipRequiredDialog } from "@/components/clubs/payments";
import { useAuth } from "@/components/providers/AuthProvider";
import { useAppDispatch } from "@/store";
import { useClubVisitHistory } from "@/hooks/useClubVisitHistory";
import {
  ClubInfoHeader,
  ClubTabs,
  ClubTabsList,
  ClubTabsContent,
  MembersListModal,
  ClubActionModal,
  WhatsHappeningFeed,
  UpcomingEventsSection,
  ClubInfoPanel,
  ManageClubPanel,
  JoinPanel,
  type ClubTabValue,
} from "@/components/clubs";
import { CreatePostForm, PostPrompt, PostDetailSidePanel, EditPostSidePanel } from "@/components/posts";
import type { Post } from "@/api/clubsApi";
import { FAB } from "@/components/ui/FAB";
import { CreateEventForm, EventFormContent, EventDetailSidePanel } from "@/components/events";
import {
  SidePanel,
  SidePanelContent,
  SidePanelHeader,
  SidePanelBody,
} from "@/components/ui/SidePanel";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/AlertDialog";
import { Skeleton } from "@/components/ui/Skeleton";

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Valid tab values
const validTabs = ["happening", "upcoming"] as const;

// Search params for club detail page - tab and modal state stored in URL for back button support
interface ClubDetailSearch {
  tab?: ClubTabValue;
  members?: boolean;
  payment?: string;
  connect?: string;
  action?: string;
}

export const Route = createFileRoute("/clubs/$identifier")({
  validateSearch: (search: Record<string, unknown>): ClubDetailSearch => ({
    tab: validTabs.includes(search.tab as ClubTabValue)
      ? (search.tab as ClubTabValue)
      : undefined,
    members: search.members === true || search.members === "true",
    payment: typeof search.payment === "string" ? search.payment : undefined,
    connect: typeof search.connect === "string" ? search.connect : undefined,
    action: typeof search.action === "string" ? search.action : undefined,
  }),
  component: ClubDetailPage,
});

function ClubDetailPage() {
  const { identifier } = Route.useParams();
  const { tab, members: membersModalOpen, payment, connect } = Route.useSearch();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { recordVisit } = useClubVisitHistory();

  const isUuid = UUID_REGEX.test(identifier);

  // --- Identifier resolution ---
  // If UUID, fetch club directly
  const { data: clubById, isLoading: loadingById } = useGetClubQuery(identifier, { skip: !isUuid });

  // If not UUID, fetch by invite code from club_public_data
  const { data: publicClub, isLoading: loadingByCode } = useFetchPublicClubByInviteCodeQuery(identifier, { skip: isUuid });

  // Once we have the clubId from invite code lookup, fetch the full club data
  const resolvedClubId = isUuid ? identifier : publicClub?.id;
  const { data: clubByCode, isLoading: loadingFull } = useGetClubQuery(resolvedClubId!, {
    skip: !resolvedClubId || isUuid,
  });

  const club = isUuid ? clubById : clubByCode;
  // Use full club data when available, fall back to publicClub for unauthenticated users
  const displayClub = club || publicClub;
  const isLoading = loadingById || loadingByCode || (!isUuid && !!resolvedClubId && loadingFull);

  // Tab state from URL, defaults to "happening"
  const activeTab = tab || "happening";
  const setActiveTab = (newTab: ClubTabValue) => {
    navigate({
      to: ".",
      search: {
        tab: newTab === "happening" ? undefined : newTab,
        members: membersModalOpen || undefined,
      },
      replace: true,
    });
  };

  // Modal states for FAB actions
  const [actionModalOpen, setActionModalOpen] = useState(false);
  const [postModalOpen, setPostModalOpen] = useState(false);
  const [eventModalOpen, setEventModalOpen] = useState(false);
  const [eventType, setEventType] = useState<"hang" | "event">("hang");
  const [infoPanelOpen, setInfoPanelOpen] = useState(false);
  const [managePanelOpen, setManagePanelOpen] = useState(false);

  // Post detail sidebar state
  const [selectedPostId, setSelectedPostId] = useState<string | null>(null);
  const [focusReply, setFocusReply] = useState(false);

  // Event detail sidebar state
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);

  // Event edit/delete state
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);
  const [editFormOpen, setEditFormOpen] = useState(false);
  const [deletingEvent, setDeletingEvent] = useState<EventOccurrence | null>(null);
  const [deleteEvent] = useDeleteEventMutation();

  // Edit post sidebar state
  const [editingPost, setEditingPost] = useState<Post | null>(null);

  // Control members modal via URL search params for back button support
  const setMembersModalOpen = (open: boolean) => {
    navigate({
      to: ".",
      search: {
        tab: tab,
        members: open || undefined,
      },
      replace: !open,
    });
  };

  // Track club visit for "most recent" feature (matches mobile pattern)
  useEffect(() => {
    if (resolvedClubId) {
      recordVisit(resolvedClubId);
    }
  }, [resolvedClubId, recordVisit]);

  // Check if user is a member
  const { data: membership } = useCheckClubMembershipQuery(
    { clubId: resolvedClubId!, userId: user?.id || "" },
    { skip: !user?.id || !resolvedClubId }
  );

  // Fetch club members (for role checking)
  const { data: members, isLoading: loadingMembers } = useFetchClubMembersQuery(
    { clubId: resolvedClubId!, includeUserProfiles: false },
    { skip: !user?.id || !resolvedClubId }
  );

  // Fetch user profile for pending request check
  const { data: userProfile } = useFetchCurrentUserProfileQuery(user?.id!, {
    skip: !user?.id,
  });

  // Payment: fetch public payment settings and user subscription
  const { data: paymentSettings } = useFetchPublicClubPaymentSettingsQuery(resolvedClubId!, {
    skip: !resolvedClubId,
  });
  const { data: userSubscription } = useFetchClubSubscriptionQuery(
    { clubId: resolvedClubId!, userId: user?.id || "" },
    { skip: !user?.id || !resolvedClubId }
  );

  const dispatch = useAppDispatch();

  // Handle payment return from Stripe checkout
  useEffect(() => {
    if (!resolvedClubId) return;

    if (payment === "success") {
      // Invalidate subscription cache to pick up the new subscription
      if (user?.id) {
        dispatch(
          clubsApi.util.invalidateTags([
            { type: "ClubSubscription", id: `${resolvedClubId}-${user.id}` },
          ])
        );
        // Delayed refetch to handle Stripe webhook processing delay
        const timeout = setTimeout(() => {
          dispatch(
            clubsApi.util.invalidateTags([
              { type: "ClubSubscription", id: `${resolvedClubId}-${user.id}` },
            ])
          );
        }, 3000);
        // Clean URL param
        navigate({
          to: ".",
          search: { tab, members: membersModalOpen || undefined },
          replace: true,
        });
        return () => clearTimeout(timeout);
      }
      navigate({
        to: ".",
        search: { tab, members: membersModalOpen || undefined },
        replace: true,
      });
    } else if (payment === "cancelled") {
      navigate({
        to: ".",
        search: { tab, members: membersModalOpen || undefined },
        replace: true,
      });
    }
  }, [payment, resolvedClubId]);

  // Handle Stripe Connect return - auto-open manage panel
  useEffect(() => {
    if (connect === "success" || connect === "refresh") {
      setManagePanelOpen(true);
      // Clean URL param
      navigate({
        to: ".",
        search: { tab, members: membersModalOpen || undefined },
        replace: true,
      });
    }
  }, [connect]);

  const isMember = !!membership;
  const currentUserMember = members?.find((m) => m.user_id === user?.id);
  const isOwnerOrCohost =
    currentUserMember?.role === "owner" || currentUserMember?.role === "cohost";
  const isClubOwner = !!user?.id && club?.creator_id === user.id;
  const userRole = currentUserMember?.role || null;

  // Pending request detection
  const hasPendingRequest = !!(
    resolvedClubId && userProfile?.outgoing_requests?.includes(resolvedClubId)
  );

  // Membership payment dialog: show for members who haven't completed their subscription
  const hasPaidMembership =
    userSubscription?.type === "membership" &&
    ["active", "trialing", "past_due"].includes(userSubscription?.status || "");
  const membershipRequired = !!paymentSettings?.membership_enabled;
  const shouldShowMembershipDialog =
    membershipRequired &&
    isMember &&
    !hasPaidMembership &&
    !isClubOwner &&
    !isOwnerOrCohost &&
    !isLoading &&
    !loadingMembers &&
    !!club &&
    userSubscription !== undefined;

  // Permission checks for FAB actions (matching mobile)
  const canCreatePost =
    isMember && (isOwnerOrCohost || club?.post_permission === "everyone");
  const canCreateHang =
    isMember && (isOwnerOrCohost || club?.hang_permission === "everyone");
  const canCreateEvent = isMember && isOwnerOrCohost;
  const showFAB = canCreatePost || canCreateHang || canCreateEvent;

  // Set header configuration for this page
  useHeaderConfig(
    {
      breadcrumbs: displayClub
        ? [
            { label: "All clubs", path: "/clubs" },
            { label: displayClub.name },
          ]
        : [{ label: "All clubs", path: "/clubs" }],
      actions: displayClub ? (
        <>
          {isOwnerOrCohost && (
            <button
              className="h-[40px] min-w-[88px] px-3 rounded-full bg-surface-1 text-[16px] font-medium text-text-secondary hover:bg-surface-2 transition-colors"
              onClick={() => setManagePanelOpen(true)}
            >
              Manage
            </button>
          )}
          <button
            className="size-[40px] rounded-full bg-surface-1 hover:bg-surface-2 transition-colors inline-flex items-center justify-center"
            onClick={async () => {
              const shareLink = displayClub.invite_code
                ? `https://tiny.club/join/${displayClub.invite_code}`
                : window.location.href;
              const result = await shareUrl({ url: shareLink, title: displayClub.name });
              if (result.message) {
                // Toast would be shown here
              }
            }}
            aria-label="Share"
          >
            <Icon name="arrow-up-from-bracket" size={18} className="text-text-secondary" />
          </button>
        </>
      ) : undefined,
    },
    [displayClub?.id, displayClub?.name, isOwnerOrCohost, setManagePanelOpen]
  );

  // FAB action handlers
  const handlePostClick = () => {
    setActionModalOpen(false);
    setPostModalOpen(true);
  };

  const handleHangClick = () => {
    setActionModalOpen(false);
    setEventType("hang");
    setEventModalOpen(true);
  };

  const handleGatherClick = () => {
    setActionModalOpen(false);
    setEventType("event");
    setEventModalOpen(true);
  };

  // Loading state
  if (isLoading) {
    return (
      <AppLayout>
        <PageTransition>
          <ClubDetailSkeleton />
        </PageTransition>
      </AppLayout>
    );
  }

  // Not found state - neither club data nor public data available
  if (!displayClub) {
    return (
      <AppLayout>
        <PageTransition>
          <div className="flex flex-col items-center justify-center min-h-[50vh] p-6">
            <NotFoundIcon className="w-16 h-16 text-text-tertiary mb-4" />
            <h2 className="text-xl font-medium text-text-primary mb-2">
              Club not found
            </h2>
            <p className="text-text-secondary text-center mb-6">
              The club you are looking for does not exist or has been removed.
            </p>
            <Link
              to="/clubs"
              className="text-sm font-medium text-text-primary hover:text-text-secondary transition-colors"
            >
              Back to clubs
            </Link>
          </div>
        </PageTransition>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <PageTransition>
        {/* Club Info Header with image and info side by side */}
        <ClubInfoHeader
          club={displayClub}
          memberCount={members?.length}
          onMembersClick={() => setMembersModalOpen(true)}
          onInfoClick={() => setInfoPanelOpen(true)}
          isMember={isMember}
        />

        {/* Join Panel for non-members */}
        {!isMember && (
          <JoinPanel
            club={displayClub}
            clubId={resolvedClubId}
            identifier={identifier}
            user={user}
            paymentSettings={paymentSettings}
            isPrivate={displayClub.private}
            isAcceptingMembers={
              "is_accepting_members" in displayClub
                ? displayClub.is_accepting_members !== false
                : true
            }
            hasPendingRequest={hasPendingRequest}
          />
        )}

        {/* Payment button for members who still need to pay */}
        {user?.id && isMember && paymentSettings?.membership_enabled && !hasPaidMembership && !isOwnerOrCohost && resolvedClubId && (
          <div className="px-4 mb-4">
            <div className="max-w-[680px] mx-auto">
              <ClubPaymentButton
                clubId={resolvedClubId}
                userId={user.id}
                paymentSettings={paymentSettings}
                userSubscription={userSubscription}
              />
            </div>
          </div>
        )}

        {/* Tabs - same width and padding as header content */}
        <div className={`px-4 ${!isMember ? "relative select-none" : ""}`}>
          {/* Blur overlay for non-members — captures all interaction */}
          {!isMember && (
            <div className="absolute inset-0 z-10 bg-background/30 backdrop-blur-sm rounded-xl" />
          )}

          <ClubTabs
            value={activeTab}
            onValueChange={setActiveTab}
            className="max-w-[680px] mx-auto"
          >
          <ClubTabsList />

          {/* What's Happening Tab */}
          <ClubTabsContent value="happening">
            {resolvedClubId && (
              <WhatsHappeningFeed
                clubId={resolvedClubId}
                userId={user?.id}
                onPostClick={(postId) => {
                  if (isMember) {
                    setFocusReply(false);
                    setSelectedPostId(postId);
                  }
                }}
                onCommentClick={(postId) => {
                  if (isMember) {
                    setFocusReply(true);
                    setSelectedPostId(postId);
                  }
                }}
                onEventClick={(eventId) => setSelectedEventId(eventId)}
                onEditPost={(post) => setEditingPost(post)}
                postPrompt={
                  canCreatePost && user?.id ? (
                    <PostPrompt
                      clubId={resolvedClubId}
                      currentUserId={user.id}
                      onClick={handlePostClick}
                    />
                  ) : undefined
                }
                canEditEvent={(event) => !!user?.id && event.rootEvent?.creator_id === user.id}
                canDeleteEvent={(event) => !!user?.id && (event.rootEvent?.creator_id === user.id || isOwnerOrCohost)}
                onEditEvent={(event) => {
                  if (event.rootEvent) {
                    setEditingEvent(event.rootEvent);
                    setEditFormOpen(true);
                  }
                }}
                onDeleteEvent={(event) => setDeletingEvent(event)}
              />
            )}
          </ClubTabsContent>

          {/* Upcoming Tab */}
          <ClubTabsContent value="upcoming">
            {resolvedClubId && (
              <UpcomingEventsSection
                    clubId={resolvedClubId}
                    userId={user?.id}
                    onEventClick={(eventId) => setSelectedEventId(eventId)}
                    canEditEvent={(event) => !!user?.id && event.rootEvent?.creator_id === user.id}
                    canDeleteEvent={(event) => !!user?.id && (event.rootEvent?.creator_id === user.id || isOwnerOrCohost)}
                    onEditEvent={(event) => {
                      if (event.rootEvent) {
                        setEditingEvent(event.rootEvent);
                        setEditFormOpen(true);
                      }
                    }}
                    onDeleteEvent={(event) => setDeletingEvent(event)}
                  />
            )}
          </ClubTabsContent>
          </ClubTabs>
        </div>

        {/* Members List Modal - only for members */}
        {isMember && club && (
          <MembersListModal
            club={club}
            open={membersModalOpen ?? false}
            onOpenChange={setMembersModalOpen}
          />
        )}

        {/* Club Info Panel */}
        {club && (
          <ClubInfoPanel
            club={club}
            open={infoPanelOpen}
            onOpenChange={setInfoPanelOpen}
            currentUserId={user?.id}
          />
        )}

        {/* Manage Club Panel */}
        {isOwnerOrCohost && club && (
          <ManageClubPanel
            club={club}
            open={managePanelOpen}
            onOpenChange={setManagePanelOpen}
          />
        )}
      </PageTransition>

      {/* FAB - outside PageTransition for fixed positioning */}
      {showFAB && <FAB onClick={() => setActionModalOpen(true)} />}

      {/* Club Action Modal */}
      {isMember && club && resolvedClubId && (
        <ClubActionModal
          open={actionModalOpen}
          onOpenChange={setActionModalOpen}
          clubId={resolvedClubId}
          club={club}
          userRole={userRole}
          isMember={isMember}
          onPostClick={handlePostClick}
          onHangClick={handleHangClick}
          onGatherClick={handleGatherClick}
        />
      )}

      {/* Create Post Form */}
      {user?.id && isMember && resolvedClubId && (
        <CreatePostForm
          clubId={resolvedClubId}
          authorId={user.id}
          open={postModalOpen}
          onOpenChange={setPostModalOpen}
        />
      )}

      {/* Create Event Form */}
      {user?.id && isMember && resolvedClubId && (
        <CreateEventForm
          clubId={resolvedClubId}
          creatorId={user.id}
          eventType={eventType}
          open={eventModalOpen}
          onOpenChange={setEventModalOpen}
        />
      )}

      {/* Edit Event Side Panel (from overflow menu) */}
      {editingEvent && resolvedClubId && user?.id && (
        <SidePanel
          open={editFormOpen}
          onOpenChange={(open) => {
            setEditFormOpen(open);
            if (!open) setEditingEvent(null);
          }}
        >
          <SidePanelContent>
            <SidePanelHeader
              title={editingEvent.event_type === EventType.hang ? "Edit Hang" : "Edit Event"}
              onClose={() => {
                setEditFormOpen(false);
                setEditingEvent(null);
              }}
            />
            <SidePanelBody>
              <EventFormContent
                clubId={resolvedClubId}
                creatorId={editingEvent.creator_id ?? ""}
                eventType={editingEvent.event_type === EventType.hang ? "hang" : "event"}
                eventId={editingEvent.id}
                existingEvent={editingEvent}
                onClose={() => {
                  setEditFormOpen(false);
                  setEditingEvent(null);
                }}
                onDeleted={() => {
                  if (selectedEventId) setSelectedEventId(null);
                }}
                showHeader={false}
              />
            </SidePanelBody>
          </SidePanelContent>
        </SidePanel>
      )}

      {/* Delete Event Confirmation Dialog */}
      <AlertDialog
        open={!!deletingEvent}
        onOpenChange={(open) => { if (!open) setDeletingEvent(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete {deletingEvent?.rootEvent?.title || "this event"}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. The event and all associated RSVPs
              will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red text-white hover:bg-red/90"
              onClick={async () => {
                if (!deletingEvent?.rootEvent?.club_id) return;
                try {
                  await deleteEvent({
                    eventId: deletingEvent.rootEvent.id,
                    clubId: deletingEvent.rootEvent.club_id,
                  }).unwrap();
                  if (selectedEventId === deletingEvent.id) {
                    setSelectedEventId(null);
                  }
                  setDeletingEvent(null);
                } catch (error) {
                  console.error("Failed to delete event:", error);
                }
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Post Detail Side Panel */}
      {isMember && resolvedClubId && (
        <PostDetailSidePanel
          postId={selectedPostId}
          clubId={resolvedClubId}
          currentUserId={user?.id}
          open={!!selectedPostId}
          focusReply={focusReply}
          onOpenChange={(open) => {
            if (!open) {
              setSelectedPostId(null);
              setFocusReply(false);
            }
          }}
          onEdit={(post) => {
            setSelectedPostId(null);
            setEditingPost(post);
          }}
        />
      )}

      {/* Edit Post Side Panel */}
      {user?.id && isMember && resolvedClubId && (
        <EditPostSidePanel
          post={editingPost}
          clubId={resolvedClubId}
          authorId={user.id}
          open={!!editingPost}
          onOpenChange={(open) => {
            if (!open) setEditingPost(null);
          }}
        />
      )}

      {/* Event Detail Side Panel */}
      {resolvedClubId && selectedEventId && (
        <EventDetailSidePanel
          eventId={selectedEventId}
          open={!!selectedEventId}
          onOpenChange={(open) => {
            if (!open) setSelectedEventId(null);
          }}
        />
      )}

      {/* Membership Required Dialog - non-dismissable for members without subscription */}
      {resolvedClubId && (
        <MembershipRequiredDialog
          open={shouldShowMembershipDialog}
          clubId={resolvedClubId}
        />
      )}
    </AppLayout>
  );
}

function ClubDetailSkeleton() {
  return (
    <div className="p-6 lg:pl-20">
      <div className="max-w-4xl mx-auto flex flex-col md:flex-row gap-6">
        {/* Image skeleton */}
        <Skeleton className="w-full md:w-64 h-48 md:h-64 rounded-2xl" />

        {/* Info skeleton */}
        <div className="flex-1 space-y-4">
          <Skeleton className="h-8 w-48" />
          <div className="flex gap-2">
            <Skeleton className="h-8 w-24 rounded-full" />
            <Skeleton className="h-8 w-24 rounded-full" />
          </div>
          <Skeleton className="h-16 w-full" />
        </div>
      </div>

      {/* Tabs skeleton */}
      <div className="max-w-3xl mx-auto mt-6">
        <div className="flex gap-2 p-1 rounded-full bg-surface-2">
          <Skeleton className="flex-1 h-10 rounded-full" />
          <Skeleton className="flex-1 h-10 rounded-full" />
        </div>

        {/* Content skeleton */}
        <div className="p-4 space-y-4">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-32 w-full rounded-xl" />
          ))}
        </div>
      </div>
    </div>
  );
}

// Icon components
function NotFoundIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  );
}
